import axios from 'axios';

const API_URL = process.env.REACT_APP_API_BASE_URL || 'http://138.199.234.61:8080';

class AuthService {
  constructor() {
    this.authToken = localStorage.getItem('authToken');
    this.userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
    this.pendingToken = null;
    this.mfaState = null;
    this._authFailureHandling = false;

    // Global axios response interceptor to handle auth failures consistently
    const self = this;
    try {
      axios.interceptors.response.use(
        (response) => response,
        async (error) => {
          const cfg = error?.config || {};
          if (cfg.skipAuthInterceptor) {
            return Promise.reject(error);
          }
          const status = error?.response?.status;
          const isNetworkError = !error?.response && (error?.code === 'ERR_NETWORK' || error?.code === 'ERR_CONNECTION_REFUSED' || (error?.message || '').toLowerCase().includes('network'));
          const url = cfg.url || '';
          const isLogoutReq = typeof url === 'string' && url.includes('/auth/logout');

          if (status === 401 || status === 403) {
            if (!isLogoutReq && !self._authFailureHandling) {
              self._authFailureHandling = true;
              try { self.clearAuth(); } catch (_) {}
              try { if (typeof window !== 'undefined') window.location.href = '/pages/login'; } catch (_) {}
              setTimeout(() => { self._authFailureHandling = false; }, 500);
            }
          } else if (isNetworkError) {
            if (self.getToken() && !self._authFailureHandling) {
              self._authFailureHandling = true;
              try { self.clearAuth(); } catch (_) {}
              try { if (typeof window !== 'undefined') window.location.href = '/pages/login'; } catch (_) {}
              setTimeout(() => { self._authFailureHandling = false; }, 500);
            }
          }

          return Promise.reject(error);
        }
      );
    } catch (_) {
      // noop if interceptors cannot be registered in non-browser/test environments
    }
  }

  // Notify app about auth changes (same-tab)
  dispatchAuthChanged() {
    try {
      window.dispatchEvent(new Event('auth-changed'));
    } catch (_) {
      // noop in non-browser environments
    }
  }

  setAuthToken(token, user) {
    this.authToken = token;
    localStorage.setItem('authToken', token);
    if (user) {
      this.userInfo = user;
      localStorage.setItem('userInfo', JSON.stringify(user));
    }
    this.dispatchAuthChanged();
  }

  clearAuth() {
    this.authToken = null;
    this.userInfo = null;
    localStorage.removeItem('authToken');
    localStorage.removeItem('userInfo');
    this.dispatchAuthChanged();
  }

  // Synchronous helpers expected by useAuth
  isAuthenticated() {
    return Boolean(this.authToken || localStorage.getItem('authToken'));
  }

  getCurrentUser() {
    return this.userInfo || JSON.parse(localStorage.getItem('userInfo') || 'null');
  }

  getToken() {
    return this.authToken || localStorage.getItem('authToken');
  }

  // Role helpers for backward compatibility
  getUserRole() {
    const user = this.getCurrentUser();
    return user && user.role ? user.role : null;
  }

  hasRole(requiredRole) {
    if (!requiredRole) return true;
    const role = this.getUserRole();
    if (!role) return false;

    // Role hierarchy: admin >= superuser >= user
    const rank = { user: 1, superuser: 2, admin: 3 };
    const userLevel = rank[role] || 0;

    if (Array.isArray(requiredRole)) {
      // Allow access if user meets ANY of the required roles
      return requiredRole.some(r => userLevel >= (rank[r] || Number.POSITIVE_INFINITY));
    }
    return userLevel >= (rank[requiredRole] || Number.POSITIVE_INFINITY);
  }

  // Auth flows
  async login(username, password) {
    const url = `${API_URL}/auth/login`;
    const body = { username, password };
    const res = await axios.post(url, body);
    const data = res.data;

    if (data.mfa_required) {
      this.pendingToken = data.pending_token;
      this.mfaState = 'verify';
      return { ...data, success: true };
    }

    if (data.mfa_setup_required) {
      this.pendingToken = data.pending_token;
      this.mfaState = 'setup';
      return { ...data, success: true };
    }

    if (data.token) {
      this.setAuthToken(data.token, data.user);
      this.pendingToken = null;
      this.mfaState = null;
      return { ...data, success: true };
    }

    return data || { success: false, message: 'Login failed' };
  }

  async verifyMfa(code) {
    const url = `${API_URL}/auth/mfa/verify`;
    const body = { code: String(code), pending_token: this.pendingToken };
    const res = await axios.post(url, body);
    const data = res.data;
    if (data.token) {
      this.setAuthToken(data.token, data.user);
      this.pendingToken = null;
      this.mfaState = null;
      return { success: true };
    }
    return { success: false, message: data.message || 'MFA verification failed' };
  }

  // Alias expected by login page
  async verifyMfaCode(code) {
    return this.verifyMfa(code);
  }

  async verifyMfaSetup(code) {
    const url = `${API_URL}/auth/mfa/setup/verify`;
    const body = { code: String(code), pending_token: this.pendingToken };
    const res = await axios.post(url, body);
    const data = res.data;
    if (data.token) {
      this.setAuthToken(data.token, data.user);
      this.pendingToken = null;
      this.mfaState = null;
      return { success: true };
    }
    return { success: false, message: data.message || 'MFA setup verification failed' };
  }

  async validateToken() {
    const headers = this.getAuthHeaders();
    const primary = `${API_URL}/auth/validate`;
    // Build compatibility alternates depending on base URL shape
    const base = (API_URL || '').replace(/\/+$/, '');
    const alternates = [];
    // If base already ends with /api, also try without it
    if (base.endsWith('/api')) {
      alternates.push(`${base.slice(0, -4)}/auth/validate`);
    } else {
      alternates.push(`${base}/api/auth/validate`);
    }

    const tryValidate = async (url) => {
      try {
        const res = await axios.get(url, { headers });
        const data = res.data;
        if (data && data.valid) {
          if (data.user) {
            this.userInfo = data.user;
            localStorage.setItem('userInfo', JSON.stringify(data.user));
          }
          return data; // { valid, user }
        }
        return { valid: false, user: null };
      } catch (e) {
        // For 401/403, clear auth and hint UI to login
        const hadToken = Boolean(this.getToken());
        const isNetworkError = !e?.response && (e?.code === 'ERR_NETWORK' || e?.code === 'ERR_CONNECTION_REFUSED' || (e?.message || '').toLowerCase().includes('network'));
        if (hadToken && (e?.response?.status === 401 || e?.response?.status === 403 || isNetworkError)) {
          try { this.clearAuth(); } catch (_) {}
          try { if (typeof window !== 'undefined') window.location.href = '/pages/login'; } catch (_) {}
        }
        // Propagate error to allow fallback attempt for 5xx / network
        throw e;
      }
    };

    // Try primary, then alternates on network/5xx errors or 502 from proxy
    try {
      return await tryValidate(primary);
    } catch (e) {
      const status = e?.response?.status;
      const shouldFallback = !status || (status >= 500 && status <= 599) || status === 502;
      if (shouldFallback) {
        for (const alt of alternates) {
          try {
            return await tryValidate(alt);
          } catch (_) {
            // continue to next alternate
          }
        }
      }
      return { valid: false, user: null };
    }
  }

  getAuthHeaders() {
    const headers = {};
    const token = this.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    const user = this.getCurrentUser();
    if (user) {
      // Provide user context headers to help backend scope data per user/org
      if (user.username || user.name || user.email) {
        headers['x-user-name'] = user.username || user.name || user.email;
      }
      if (user.id != null) headers['x-user-id'] = String(user.id);
      if (user.role) headers['x-user-role'] = String(user.role);
      const org = user.organisation || user.organization || user.org || user.orgId || user.org_id;
      if (org != null) headers['x-user-org'] = String(org);
    }
    return headers;
  }

  // Generic authenticated fetch call (for external services like pentest-api)
  async authenticatedCall(url, options = {}) {
    const token = this.getToken();
    const userInfo = this.getCurrentUser();

    if (!token) {
      throw new Error('No authentication token available');
    }

    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    };

    // Attach user context headers
    if (userInfo) {
      if (userInfo.username || userInfo.name || userInfo.email) {
        headers['x-user-name'] = userInfo.username || userInfo.name || userInfo.email;
      }
      if (userInfo.id != null) headers['x-user-id'] = String(userInfo.id);
      if (userInfo.role) headers['x-user-role'] = String(userInfo.role);
      const org = userInfo.organisation || userInfo.organization || userInfo.org || userInfo.orgId || userInfo.org_id;
      if (org != null) headers['x-user-org'] = String(org);
    }

    const doFetch = async (targetUrl) => {
      return fetch(targetUrl, { ...options, headers });
    };

    let response = await doFetch(url);

    if (response.status === 401) {
      await this.logout();
      window.location.href = '/pages/login';
      throw new Error('Authentication failed');
    }

    // Pentest/tools gateway compatibility fallback: retry with alternate prefixes on 404 or HTML responses
    // This helps when production proxy uses /pentest/api or strips /api → /pentest, or serves SPA HTML instead of JSON
    const isHtml = (() => {
      try {
        const ct = response.headers?.get?.('content-type') || response.headers?.get?.('Content-Type') || '';
        return typeof ct === 'string' && ct.toLowerCase().includes('text/html');
      } catch (_) {
        return false;
      }
    })();

    if ((response.status === 404 || (response.ok && isHtml)) && typeof url === 'string') {
      try {
        // Normalize multiple candidate rewrites for both pentest and tools gateways
        const candidates = [];
        const hasPentest = url.includes('/api/pentest') || url.includes('/pentest/');
        const hasTools = url.includes('/api/tools') || url.includes('/tools/');

        if (url.includes('/api/pentest')) {
          candidates.push(url.replace('/api/pentest', '/pentest/api'));
          candidates.push(url.replace('/api/pentest', '/pentest'));
        } else if (url.includes('/pentest/api')) {
          candidates.push(url.replace('/pentest/api', '/api/pentest'));
        } else if (url.includes('/pentest/') && !url.includes('/pentest/api/')) {
          candidates.push(url.replace('/pentest/', '/pentest/api/'));
          candidates.push(url.replace('/pentest/', '/api/pentest/'));
        }

        if (url.includes('/api/tools')) {
          candidates.push(url.replace('/api/tools', '/tools'));
        } else if (url.includes('/tools/') && !url.includes('/api/tools/')) {
          candidates.push(url.replace('/tools/', '/api/tools/'));
        }

        for (const alt of candidates) {
          try {
            const altResp = await doFetch(alt);
            const altCt = altResp.headers?.get?.('content-type') || altResp.headers?.get?.('Content-Type') || '';
            const altIsHtml = typeof altCt === 'string' && altCt.toLowerCase().includes('text/html');
            // Found a working alternative (non-404 and not HTML when expecting API)
            if (altResp.status !== 404 && !(altResp.ok && altIsHtml && (hasPentest || hasTools))) {
              response = altResp;
              break;
            }
          } catch (_) {
            // ignore network errors on fallback; proceed to next candidate
          }
        }
      } catch (_) {
        // noop if URL rewrites fail
      }
    }

    return response;
  }

  async logout() {
    try {
      await axios.post(`${API_URL}/auth/logout`, {}, { headers: this.getAuthHeaders(), skipAuthInterceptor: true });
    } catch (_) {
      // ignore logout errors
    } finally {
      this.clearAuth();
    }
  }

  // --- Admin user management & org helpers (unchanged) ---
  async createUser(arg1, password, role, email, phone, organisation, site, address) {
    let userData;

    if (typeof arg1 === 'object' && arg1 !== null) {
      userData = arg1;
    } else {
      userData = { username: arg1, password, role, email, phone, organisation, site, address };
    }

    // Backend expects POST /auth/create-user (aliased also under /api/auth/create-user)
    const res = await axios.post(`${API_URL}/auth/create-user`, userData, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  async getAllUsers() {
    const res = await axios.get(`${API_URL}/auth/users`, {
      headers: this.getAuthHeaders(),
    });
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.users)) return data.users;
    return [];
  }

  async deleteUser(userId) {
    const res = await axios.delete(`${API_URL}/auth/users/${userId}`, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  async updateUserPassword(userId, password) {
    const res = await axios.put(
      `${API_URL}/auth/users/${userId}/password`,
      { password },
      { headers: this.getAuthHeaders() }
    );
    return res.data;
  }

  async updateUserRole(userId, role) {
    const res = await axios.put(
      `${API_URL}/auth/users/${userId}/role`,
      { role },
      { headers: this.getAuthHeaders() }
    );
    return res.data;
  }

  async updateUserProfile(userId, emailOrProfile, phone, organisation, site, address) {
    let profile;

    if (typeof emailOrProfile === 'object' && emailOrProfile !== null) {
      profile = emailOrProfile;
    } else {
      profile = { email: emailOrProfile, phone, organisation, site, address };
    }

    const res = await axios.put(`${API_URL}/auth/users/${userId}/profile`, profile, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  async getUserDetails(userId) {
    const res = await axios.get(`${API_URL}/auth/users/${userId}`, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  async getStatistics() {
    const res = await axios.get(`${API_URL}/system/statistics`, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  async getActivityLogs(params = {}) {
    const res = await axios.get(`${API_URL}/system/activity-logs`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return res.data;
  }

  async getLoginAttempts(params = {}) {
    const res = await axios.get(`${API_URL}/system/login-attempts`, {
      headers: this.getAuthHeaders(),
      params,
    });
    return res.data;
  }

  // New: reset user's MFA
  async resetUserMfa(userId) {
    const res = await axios.post(
      `${API_URL}/auth/users/${userId}/mfa/reset`,
      {},
      { headers: this.getAuthHeaders() }
    );
    return res.data;
  }

  // Organization quota API
  async getOrgQuota() {
    const res = await axios.get(`${API_URL}/system/org/quota`, {
      headers: this.getAuthHeaders(),
    });
    return res.data;
  }

  // Organizations list (admin)
  async getOrganizations() {
    const res = await axios.get(`${API_URL}/admin/organizations`, {
      headers: this.getAuthHeaders(),
    });
    const data = res.data;
    if (Array.isArray(data)) return data;
    if (data && Array.isArray(data.organizations)) return data.organizations;
    return [];
  }
}

const authService = new AuthService();
export default authService;